import { FormEvent, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    REPLAY_KIND,
    REPLAY_OUTCOME,
    REPLAY_SKIP_HINT,
    ReplayKind,
    parseIds,
} from '../../api/replay';
import { ReplayMonitor } from '../../hooks/useReplay';
import { CollapsibleSection } from '../Section/CollapsibleSection';
import './Replay.scss';

export interface IReplayPanelProps {
    monitor: ReplayMonitor;
    open: boolean;
    onToggle: () => void;
}

const KINDS: ReplayKind[] = [
    'Invoice',
    'CreditNote',
    'Customer',
    'Payment',
    'Refund',
];

const INTRO =
    'Voor objecten die wel in Chargebee staan maar die de connector nooit heeft ontvangen. Ophalen leest ze bij Chargebee en laat zien wat er zou gebeuren; aanbieden aan de connector is een aparte knop daarna. Aangeboden loopt het object de gewone route, als de webhook die is misgelopen: klant, regels, PDF en het juiste objecttype.';

const KIND_HINT =
    'Welk soort object er opgehaald en aangeboden wordt. Betalingen en terugbetalingen zijn eigen objecten met een eigen transactie-id: een aangeboden factuur brengt zijn betalingen niet mee, want dat waren aparte webhooks die net zo apart zijn misgelopen.';

const PAYMENTS_HINT =
    'Vraagt Chargebee per factuur welke betalingen eraan hangen, en zoekt elk transactie-id op in de koppeltabel. Leest alleen. Dit is de stap na het aanbieden van facturen: pas als de factuur in Exact staat, kan de betaling erop landen.';

/** A day out of the API, written the way a Dutch reader reads one. */
const dayLabel = (iso?: string | null): string => {
    if (!iso) {
        return '';
    }

    const parsed = Date.parse(iso);

    return Number.isNaN(parsed)
        ? ''
        : new Date(parsed).toLocaleDateString('nl-NL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
          });
};

/** Chargebee reports money in cents, and nobody reads an invoice total in cents. */
const amountLabel = (cents?: number | null): string =>
    cents === null || cents === undefined
        ? ''
        : (cents / 100).toLocaleString('nl-NL', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
          });

/** The first day of the month a year ago, as the date inputs want it. */
const isoDay = (date: Date): string =>
    `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(
        2,
        '0'
    )}-${`${date.getDate()}`.padStart(2, '0')}`;

/**
 * Fetching objects back out of Chargebee.
 *
 * Two steps, and the first one writes nothing. A list of numbers pasted out of a spreadsheet
 * is the input nobody has checked, and the two answers that decide whether a replay is a
 * good idea - does Chargebee still have this, is it already in Exact - can only be had by
 * asking. So the panel asks, shows what came back per number, and only then offers the
 * button that puts anything on a queue.
 *
 * The box of ids comes first and the kind stands next to it, because that is the pair
 * somebody works with: the kind decides which resource an id is looked up in. Underneath it
 * sits the other way in - walking Chargebee by period - for when there is no list to paste at
 * all, and it follows the same kind.
 */
export const ReplayPanel = (props: IReplayPanelProps) => {
    const { monitor } = props;

    const [kind, setKind] = useState<ReplayKind>('Invoice');
    const [text, setText] = useState<string>('');

    // Ninety days back by default: long enough to cover a backlog somebody has been living
    // with, short enough that the first search answers in seconds rather than walking a year.
    const [fromDay, setFromDay] = useState<string>(() => {
        const start = new Date();

        start.setDate(start.getDate() - 90);

        return isoDay(start);
    });

    const [toDay, setToDay] = useState<string>(() => isoDay(new Date()));
    const [skipNotIssued, setSkipNotIssued] = useState<boolean>(true);

    const ids = useMemo(() => parseIds(text), [text]);

    const found = monitor.found;

    // A fetch started elsewhere (the trace) is about its own kind; the panel follows it, so the
    // labels and the button that offers are about what was fetched.
    const { resultKind } = monitor;

    useEffect(() => {
        if (resultKind) {
            setKind(resultKind);
        }
    }, [resultKind]);

    const runSearch = () => {
        void monitor.search(
            kind,
            fromDay ? new Date(fromDay) : null,
            toDay ? new Date(toDay) : null,
            skipNotIssued
        );
    };

    /**
     * The payments on the invoices in the box, out of Chargebee itself.
     *
     * The question that follows a replay of invoices, and the one thing the date walk cannot
     * answer: the transaction list has no filter that says "belonging to this invoice", so
     * Chargebee is asked per invoice instead. What comes back lands in the same place a date
     * walk lands, so the same button carries it into the box of ids.
     */
    const runPaymentSearch = () => {
        if (ids.length > 0) {
            void monitor.searchPayments(ids, true);
        }
    };

    /**
     * Moves what the search found into the box of ids, where the ordinary flow takes over.
     *
     * Deliberately a step rather than an automatic hand-off. The search answers "which ones
     * has Chargebee got that Exact has not", and that is a list somebody should look at
     * before it becomes the input to something that writes.
     */
    const takeFound = () => {
        if (!found) {
            return;
        }

        setKind(found.kind);
        setText(found.items.map((item) => item.id).join('\n'));
        monitor.reset();
    };

    /** The ids the check said are worth fetching, which is what the real run gets. */
    const worthFetching = useMemo(
        () =>
            (monitor.result?.results ?? [])
                .filter((item) => item.outcome === 'WouldQueue')
                .map((item) => item.id),
        [monitor.result]
    );

    const check = (event: FormEvent) => {
        event.preventDefault();

        if (ids.length > 0) {
            void monitor.check(kind, ids);
        }
    };

    const offer = () => {
        if (worthFetching.length === 0) {
            return;
        }

        const offered = resultKind ?? kind;

        if (
            !window.confirm(
                `${worthFetching.length} ${REPLAY_KIND[offered].plural} aanbieden aan de connector? Ze worden eerst opnieuw bij Chargebee opgehaald.`
            )
        ) {
            return;
        }

        void monitor.run(offered, worthFetching);
    };

    const result = monitor.result;

    /** Whether the found list has an invoice next to every id, which only payments do. */
    const showsInvoice = !!found?.items.some((item) => !!item.invoiceId);

    return (
        <CollapsibleSection
            id="replay-panel"
            className="replay-panel"
            title="Zoek in Chargebee"
            hint={INTRO}
            open={props.open}
            onToggle={props.onToggle}
            busy={monitor.busy || monitor.searching}
        >
            {/*
                The kind next to the box it governs.

                It sat in the header, on the far right, where it read as a property of the
                panel rather than of the field underneath it - and a search for invoices was
                the only search anybody found. It governs both halves: which resource a
                pasted id is looked up in, and which Chargebee list the period search walks.
            */}
            <form className="replay-panel__form" onSubmit={check}>
                <div className="replay-panel__entry">
                    <label className="replay-panel__kind" title={KIND_HINT}>
                        <span>Soort</span>
                        <select
                            className="form-select filter filter--sm"
                            value={kind}
                            onChange={(event) => {
                                setKind(event.target.value as ReplayKind);
                                monitor.reset();
                                monitor.clearSearch();
                            }}
                            disabled={monitor.busy || monitor.searching}
                        >
                            {KINDS.map((option) => (
                                <option key={option} value={option}>
                                    {REPLAY_KIND[option].label}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="visually-hidden" htmlFor="replay-ids">
                        Ids, één per regel
                    </label>

                    <textarea
                        id="replay-ids"
                        className="form-control replay-panel__ids"
                        rows={5}
                        placeholder={`Eén per regel. ${REPLAY_KIND[kind].hint}`}
                        value={text}
                        onChange={(event) => setText(event.target.value)}
                        disabled={monitor.busy}
                    />
                </div>

                <div className="replay-panel__actions">
                    <button
                        type="submit"
                        className="btn btn-sm btn-outline-primary"
                        disabled={monitor.busy || ids.length === 0}
                    >
                        {monitor.busy && monitor.checkedOnly ? (
                            <FontAwesomeIcon icon="spinner" spin />
                        ) : (
                            <FontAwesomeIcon icon="magnifying-glass" />
                        )}
                        <span className="ms-2">
                            Ophalen bij Chargebee
                            {ids.length > 0 && ` (${ids.length})`}
                        </span>
                    </button>

                    {/*
                        The bridge from invoices to their payments, next to the box that
                        holds the invoice numbers - because that is where somebody stands
                        when the question comes up. It reads only, like everything on the
                        left of the button that writes.
                    */}
                    {kind === 'Invoice' && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={runPaymentSearch}
                            disabled={monitor.searching || ids.length === 0}
                            title={PAYMENTS_HINT}
                        >
                            {monitor.searching ? (
                                <FontAwesomeIcon icon="spinner" spin />
                            ) : (
                                <FontAwesomeIcon icon="link" />
                            )}
                            <span className="ms-2">
                                Betalingen bij deze facturen
                            </span>
                        </button>
                    )}

                    <span className="replay-panel__safe">
                        Ophalen leest alleen: er verandert niets in Chargebee, in
                        Exact of in de queue.
                    </span>
                </div>
            </form>

            {/*
                Finding them, for when there is no list to paste.

                A payment failing on an invoice names it in a log line, which is how the first
                batch was found - but that only turns up the ones something else complained
                about. An invoice nobody paid leaves no trace anywhere in the connector,
                because the connector never received it. Chargebee is the only place that
                knows the full list, so this walks it and holds every id against the coupling
                table.
            */}
            <details className="replay-find" open={!!found}>
                <summary className="replay-find__summary">
                    Geen lijst? Zoek {REPLAY_KIND[kind].plural} op periode
                </summary>

                <div className="replay-find__controls">
                    <label className="replay-find__field">
                        <span>Van</span>
                        <input
                            type="date"
                            className="form-control filter filter--sm"
                            value={fromDay}
                            onChange={(event) => setFromDay(event.target.value)}
                            disabled={monitor.searching}
                        />
                    </label>

                    <label className="replay-find__field">
                        <span>Tot en met</span>
                        <input
                            type="date"
                            className="form-control filter filter--sm"
                            value={toDay}
                            onChange={(event) => setToDay(event.target.value)}
                            disabled={monitor.searching}
                        />
                    </label>

                    <label
                        className="replay-find__check"
                        title={REPLAY_SKIP_HINT[kind]}
                    >
                        <input
                            type="checkbox"
                            checked={skipNotIssued}
                            onChange={(event) =>
                                setSkipNotIssued(event.target.checked)
                            }
                            disabled={monitor.searching}
                        />
                        <span>{REPLAY_KIND[kind].skipLabel}</span>
                    </label>

                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={runSearch}
                        disabled={monitor.searching}
                    >
                        {monitor.searching ? (
                            <FontAwesomeIcon icon="spinner" spin />
                        ) : (
                            <FontAwesomeIcon icon="magnifying-glass" />
                        )}
                        {/*
                            Named after what it searches rather than just "zoeken": the box
                            above has a button by that name, and it looks up the ids you
                            already have instead of walking a period.
                        */}
                        <span className="ms-2">Zoek op periode</span>
                    </button>

                    {monitor.searching && (
                        <button
                            type="button"
                            className="requeue-link requeue-link--muted"
                            onClick={monitor.stopSearch}
                        >
                            Stoppen
                        </button>
                    )}
                </div>

                <p className="replay-find__note">
                    Leest alleen. Chargebee wordt pagina voor pagina doorlopen
                    en elk id wordt in de koppeltabel opgezocht; er verandert
                    niets.
                </p>

                {monitor.searching && (
                    <p className="replay-find__progress">
                        <FontAwesomeIcon icon="spinner" spin className="me-2" />
                        {found
                            ? `${found.scanned} doorzocht, ${found.missing} nog niet in Exact`
                            : 'Bezig met de eerste pagina'}
                        {monitor.searchedPages > 0 &&
                            ` (${monitor.searchedPages} blok(ken))`}
                    </p>
                )}

                {found && !monitor.searching && (
                    <div className="replay-find__result">
                        <p className="replay-find__tally">
                            <strong>{found.missing}</strong> van de{' '}
                            {found.scanned} gevonden{' '}
                            {REPLAY_KIND[found.kind].plural} staan nog niet in
                            Exact.
                            {found.inExact > 0 &&
                                ` ${found.inExact} staat er wel.`}
                            {found.notIssued > 0 &&
                                ` ${
                                    found.notIssued
                                } overgeslagen (${REPLAY_KIND[
                                    found.kind
                                ].skipLabel.toLowerCase()}).`}
                            {found.ofInvoices > 0 &&
                                ` Gezocht bij ${found.ofInvoices} factu${
                                    found.ofInvoices === 1 ? 'ur' : 'ren'
                                }.`}
                        </p>

                        {/*
                            The invoices that produced nothing, named rather than counted.
                            Twelve numbers in and four payments out reads as an answer, and
                            without this there is no telling which eight had no payment from
                            which eight Chargebee never heard of.
                        */}
                        {found.unanswered.length > 0 && (
                            <p className="replay-find__unanswered">
                                Geen betaling gevonden bij{' '}
                                {found.unanswered.length} factu
                                {found.unanswered.length === 1
                                    ? 'ur'
                                    : 'ren'}:{' '}
                                <span
                                    className="replay-find__unanswered-ids"
                                    title={found.unanswered.join('\n')}
                                >
                                    {found.unanswered.slice(0, 12).join(', ')}
                                    {found.unanswered.length > 12 && ' ...'}
                                </span>
                                . Die staat er in Chargebee niet, of er hangt
                                geen geslaagde betaling aan.
                            </p>
                        )}

                        {found.stoppedEarly && found.nextOffset && (
                            <p className="replay-find__more">
                                Er is meer: het zoeken is gestopt na{' '}
                                {found.scanned} objecten.
                                <button
                                    type="button"
                                    className="requeue-link ms-2"
                                    onClick={() => void monitor.searchMore()}
                                >
                                    Verder zoeken
                                </button>
                            </p>
                        )}

                        {found.missing > 0 && (
                            <div className="replay-find__actions">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={takeFound}
                                >
                                    Zet deze {found.missing}{' '}
                                    {REPLAY_KIND[found.kind].plural} in de lijst
                                    hierboven
                                </button>

                                <span className="replay-find__hint">
                                    Daarna zoeken en verwerken, net als bij een
                                    geplakte lijst.
                                </span>
                            </div>
                        )}

                        {found.items.length > 0 && (
                            <div className="replay-find__table-wrap">
                                <table className="replay-result__table">
                                    <thead>
                                        <tr>
                                            <th>Id</th>
                                            {showsInvoice && <th>Factuur</th>}
                                            <th>Datum</th>
                                            <th>Status</th>
                                            <th>Klant</th>
                                            <th>Bedrag</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {found.items.map((item) => (
                                            <tr key={item.id}>
                                                <td>
                                                    <code>{item.id}</code>
                                                </td>
                                                {showsInvoice && (
                                                    <td>
                                                        <code>
                                                            {item.invoiceId}
                                                        </code>
                                                    </td>
                                                )}
                                                <td>{dayLabel(item.date)}</td>
                                                <td>{item.status}</td>
                                                <td>{item.customerId}</td>
                                                <td className="replay-find__amount">
                                                    {amountLabel(item.total)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </details>

            {monitor.busy && monitor.progress && (
                <div className="replay-panel__progress">
                    <div
                        className="replay-panel__progress-fill"
                        style={{
                            width: `${Math.round(
                                (monitor.progress.done /
                                    Math.max(1, monitor.progress.total)) *
                                    100
                            )}%`,
                        }}
                    />
                    <span className="replay-panel__progress-label">
                        {monitor.progress.done} van {monitor.progress.total}{' '}
                        opgezocht bij Chargebee
                    </span>
                </div>
            )}

            {monitor.error && (
                <p className="replay-panel__error" role="alert">
                    <FontAwesomeIcon
                        icon="triangle-exclamation"
                        className="me-2"
                    />
                    {monitor.error}
                </p>
            )}

            {result && (
                <div className="replay-result">
                    <div className="replay-result__head">
                        <strong>
                            {monitor.checkedOnly
                                ? `${worthFetching.length} van ${result.unique} ${REPLAY_KIND[kind].plural} kunnen aangeboden worden`
                                : `${result.queued} van ${result.unique} ${REPLAY_KIND[kind].plural} aangeboden op "${result.queueName}"`}
                        </strong>

                        {result.skipped > 0 && (
                            <span className="replay-result__aside">
                                {result.skipped} staat al in Exact
                            </span>
                        )}

                        {result.failed > 0 && (
                            <span className="replay-result__failed">
                                {result.failed} niet gevonden of mislukt
                            </span>
                        )}

                        <button
                            type="button"
                            className="requeue-link ms-auto"
                            onClick={monitor.reset}
                        >
                            Wissen
                        </button>
                    </div>

                    {/*
                        Above the table, because it is about the whole run rather than about
                        any of its rows: every id can check out and the run still be a waste
                        when the receive path throws this type away.
                    */}
                    {result.warning && (
                        <p className="replay-result__warning" role="alert">
                            <FontAwesomeIcon
                                icon="triangle-exclamation"
                                className="me-2"
                            />
                            {result.warning}
                        </p>
                    )}

                    <div className="replay-result__table-wrap">
                        <table className="replay-result__table">
                            <thead>
                                <tr>
                                    <th>Id</th>
                                    <th>In Chargebee</th>
                                    <th>Uitkomst</th>
                                    <th>Toelichting</th>
                                </tr>
                            </thead>
                            <tbody>
                                {result.results.map((item) => {
                                    const outcome =
                                        REPLAY_OUTCOME[item.outcome];

                                    return (
                                        <tr key={item.id}>
                                            <td>
                                                <code>{item.id}</code>
                                            </td>
                                            <td className="replay-result__chargebee">
                                                {item.chargebee}
                                            </td>
                                            <td>
                                                <span
                                                    className={`replay-result__outcome replay-result__outcome--${outcome.tone}`}
                                                >
                                                    {outcome.label}
                                                </span>
                                            </td>
                                            <td>
                                                {item.summary}
                                                {item.dependingId && (
                                                    <code className="ms-2">
                                                        {item.dependingId}
                                                    </code>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/*
                        The button that writes, and only after a check that says what it
                        would write. It disappears once the run is done rather than staying
                        as a way to do the same thing twice.
                    */}
                    {monitor.checkedOnly && worthFetching.length > 0 && (
                        <div className="replay-result__go">
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={offer}
                                disabled={monitor.busy}
                            >
                                {monitor.busy && (
                                    <FontAwesomeIcon
                                        icon="spinner"
                                        spin
                                        className="me-2"
                                    />
                                )}
                                {worthFetching.length}{' '}
                                {REPLAY_KIND[kind].plural} aanbieden aan de
                                connector
                            </button>

                            <span className="replay-result__go-note">
                                {kind === 'Invoice'
                                    ? 'Ze worden aangeboden alsof Chargebee de webhook alsnog stuurt. Daarna kun je met "Betalingen bij deze facturen" de betalingen erop ophalen.'
                                    : 'Ze worden aangeboden alsof Chargebee de webhook alsnog stuurt.'}
                            </span>
                        </div>
                    )}

                    {!monitor.checkedOnly && result.queued > 0 && (
                        <p className="replay-result__done">
                            Aangeboden op &quot;{result.queueName}&quot;. De
                            connector verwerkt ze in de achtergrond; volg een van de
                            nummers hierboven om te zien hoe ver het is.
                        </p>
                    )}
                </div>
            )}
        </CollapsibleSection>
    );
};
